import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { Role } from 'src/app/services/RoleModel';
import { Permission } from '../../../services/data/permission';
import { PermissionsService } from '../../../services/permissions.service';
import dayjs from 'dayjs';

export interface PermissionState {
	permissions: { [roleId: string]: Permission[] };
	permissionStrings: { [roleId: string]: string };
	isRegistered: { [roleId: string]: boolean };
}

@Injectable({
	providedIn: 'root',
})
export class RolePermissionsService {
	private readonly permissionsService = inject(PermissionsService);

	/**
	 * Loads permissions for a role and updates the state.
	 *
	 * @param role - The role to load permissions for
	 * @param state - The current permission state
	 * @param destroyRef - Destroy reference for cleanup
	 * @param maxStringLength - Maximum length for permission strings
	 */
	loadPermissionsForRole(role: Role, state: PermissionState, destroyRef: DestroyRef, maxStringLength: number): void {
		state.permissions[role.id] = [];

		this.permissionsService
			.getAllPermissionsForPermissionUser(role.id)
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe((permissionObjects) => {
				if (!permissionObjects) {
					return;
				}

				this.processPermissionObjects(permissionObjects, role, state, destroyRef, maxStringLength);
			});

		state.permissionStrings[role.id] = this.permissionsService.getPermissionsString(state.permissions[role.id], maxStringLength);
	}

	/**
	 * Processes permission objects and updates state accordingly.
	 */
	private processPermissionObjects(
		permissionObjects: PermissionObject[],
		role: Role,
		state: PermissionState,
		destroyRef: DestroyRef,
		maxStringLength: number
	): void {
		for (const permissionObj of permissionObjects) {
			const permission = this.permissionsService.permissionObjectToPermission(permissionObj);

			if (permission.codeActivatedOn) {
				this.setupRegisteredPermission(role, permission, state, destroyRef, maxStringLength);
			} else {
				this.setupUnregisteredPermission(role, permission, state, maxStringLength);
			}
		}
	}

	/**
	 * Sets up a registered permission with auth user details.
	 */
	private setupRegisteredPermission(role: Role, permission: Permission, state: PermissionState, destroyRef: DestroyRef, maxStringLength: number): void {
		state.isRegistered[role.id] = true;

		this.permissionsService
			.getAuthUserByPermissionAuthCode(permission.authCode)
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe((authUser) => {
				if (authUser) {
					permission.keyCloakUserId = authUser.keycloakUserId;
					permission.firstName = authUser.firstName;
					permission.lastName = authUser.lastName;

					state.permissions[role.id].push(permission);
					this.updatePermissionStrings(role, state, maxStringLength);
				}
			});
	}

	/**
	 * Sets up an unregistered permission.
	 */
	private setupUnregisteredPermission(role: Role, permission: Permission, state: PermissionState, maxStringLength: number): void {
		if (!state.isRegistered[role.id]) {
			state.isRegistered[role.id] = false;
		}

		state.permissions[role.id].push(permission);
		this.updatePermissionStrings(role, state, maxStringLength);
	}

	/**
	 * Updates permission strings after sorting permissions by validity date.
	 */
	private updatePermissionStrings(role: Role, state: PermissionState, maxStringLength: number): void {
		state.permissions[role.id] = this.sortPermissionsByValidity(state.permissions[role.id]);
		state.permissionStrings[role.id] = this.permissionsService.getPermissionsString(state.permissions[role.id], maxStringLength);
	}

	/**
	 * Sorts permissions by code valid until date in descending order.
	 */
	private sortPermissionsByValidity(permissions: Permission[]): Permission[] {
		return permissions.sort((a, b) => {
			return dayjs(a.codeValidUntil).isBefore(dayjs(b.codeValidUntil)) ? 1 : -1;
		});
	}
}
